import React, { useState } from 'react';
import { BsDownload } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Flex, Icon, IconButton, Skeleton } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import AddNewButton from '../../../../components/AddNewButton';
import DropdownMenu from '../../../../components/DropdownMenu';
import StackedButtonGroup from '../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import PageHeader from '../../../../components/PageHeader';
import SaveChangesModal from '../../../../components/SaveChangesModal';
import SearchGlobalSearchInput from '../../../../components/search/SearchGlobalSearchInput';
import { analyticsIds } from '../../../../constants/analyticsIds';
import { CypressIds } from '../../../../constants/cypressIds';
import { Statuses } from '../../../../constants/statuses';
import { exportListInsuredsToExcel, setCreateInsuredActiveStep } from '../../../../redux/modules/insured';
import { insuredListSearch } from '../../../../redux/modules/insuredList';
import { hideModal, showModal } from '../../../../redux/modules/modal';
import { hideTray, showTray, updateTrayProps } from '../../../../redux/modules/tray';
import { createMap } from '../../../../utils/arrayUtil';
import CreateInsuredTray from '../CreateInsuredTray';
import InsuredListFilters from '../InsuredListFilters';

const InsuredsPageHeader = ({
  searchBlueprint
}) => {
  const { companyId } = useParams();
  const insuredListPageInfo = useSelector(searchBlueprint.selectPageInfo);
  const isLoading = useSelector(searchBlueprint.selectIsLoading);
  const quickFilters = useSelector(insuredListSearch.selectQuickFilterOptions);
  const insuredStatusFilter = quickFilters.find(filter => filter.name === 'insuredStatus') ?? [];
  const insuredListSearchParamsFunction = useSelector(insuredListSearch.selectRequestParamsFunction);
  const dispatch = useDispatch();

  const options = [
    { displayName: 'Active Insureds', id: 'active' },
    { displayName: 'Archived Insureds', id: 'archived' },
    { displayName: 'All Insureds', id: 'all' }
  ];

  const optionsMap = createMap(options, 'id');

  const [selectedOption, setSelectedOption] = useState(optionsMap[Statuses.Active]);

  const getActiveStatuses = () => insuredStatusFilter.data
    .filter(status => status.value !== Statuses.Archived);

  const handleStatusFilterClick = selectedValue => {
    const selected = selectedValue.value;

    if (selected === 'all') {
      dispatch(insuredListSearch.setBaseFilters([
        { key: 'company.id', value: companyId },
        { key: 'status.keyword', value: [Statuses.Approved, Statuses.NotApproved, Statuses.Archived] }
      ]));

      dispatch(insuredListSearch.setScriptScore({ scriptScore: { source: 'doc[\'status.keyword\'].value === \'archived\' ? 1 : 5' } }));

      setSelectedOption(optionsMap.all);
    } else if (selected === Statuses.Active) {
      dispatch(insuredListSearch.setScriptScore(null));

      dispatch(insuredListSearch.setBaseFilters([
        { key: 'company.id', value: companyId },
        { key: 'status.keyword', value: [Statuses.Approved, Statuses.NotApproved] }
      ]));

      setSelectedOption(optionsMap[Statuses.Active]);
    } else {
      dispatch(insuredListSearch.setBaseFilters([
        { key: 'company.id', value: companyId },
        { key: 'status.keyword', value: [Statuses.Archived] }
      ]));

      setSelectedOption(optionsMap[Statuses.Archived]);
    }

    dispatch(insuredListSearch.getPage());
  };

  const handleDownloadListInsureds = () => {
    const insuredListSearchParams = insuredListSearchParamsFunction();

    dispatch(exportListInsuredsToExcel(companyId, insuredListSearchParams));
  };

  const showExitModal = () => dispatch(showModal(() => <SaveChangesModal changedInfo="insured information" />, {
    title: 'Exit without Saving?',
    buttons: {
      component: StackedButtonGroup,
      primaryButtonOptions: {
        onClick: () => {
          dispatch(hideModal());
          dispatch(hideTray());
          dispatch(updateTrayProps({}));
        },
        dataAnalyticsId: 'cancel-insured-creation'
      }
    }
  }));

  const showCreateInsuredTray = () => {
    dispatch(setCreateInsuredActiveStep(0));
    dispatch(showTray(
      () => <CreateInsuredTray companyId={companyId} />,
      {
        onClose: formTrayProps => {
          if (formTrayProps.formIsDirty) {
            showExitModal();
          } else {
            dispatch(hideTray());
            dispatch(updateTrayProps({}));
          }
        }
      }
    ));
  };

  return (
    <Flex
      alignItems="center"
      color="greyScale.800"
      justify="space-between"
      mb={4}
      minW="880px"
      w="100%"
    >
      <PageHeader
        subtitle={isLoading ? 'Loading...' : `${insuredListPageInfo?.total.toLocaleString('en-US') ?? 0} Total`}
        title="Insureds"
      />
      <Flex flexBasis="50%" justify="space-between">
        <Flex justify="flex-end" mr={4} w="100%">
          {!selectedOption ? (
            <Skeleton height="40px" width="100%" />
          ) : (
            <>
              <DropdownMenu
                dataAnalyticsKey={analyticsIds.insuredsListPage.filterInsuredsStatusDropdown}
                idKey="id"
                labelKey="displayName"
                mr={4}
                options={options}
                selected={selectedOption.displayName}
                variant="secondary"
                width="180px"
                onSelect={handleStatusFilterClick}
              />
              <SearchGlobalSearchInput
                cypressId={CypressIds.listInsuredsSearchInput}
                dataAnalyticsId={analyticsIds.insuredsListPage.searchInsuredsInput}
                ml={4}
                mr={4}
                searchBlueprint={searchBlueprint}
                variant="secondary"
              />
              <InsuredListFilters searchBlueprint={searchBlueprint} />
              <AddNewButton
                dataAnalyticsId={analyticsIds.insuredsListPage.createNewInsuredButton}
                tabName="New Insured"
                onClick={showCreateInsuredTray}
              />
              <IconButton
                aria-label="Download Insured List Excel"
                data-analytics-id={analyticsIds.insuredsListPage.downloadInsuredListExcelButton}
                icon={<Icon as={BsDownload} boxSize="24px" />}
                isDisabled={!!isLoading}
                ml={4}
                variant="accent"
                onClick={() => handleDownloadListInsureds()}
              />
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

InsuredsPageHeader.propTypes = {
  searchBlueprint: PropTypes.shape({
    getPage: PropTypes.func,
    selectData: PropTypes.func,
    selectPageInfo: PropTypes.func,
    selectIsLoading: PropTypes.func,
    selectIsLengthAwarePagination: PropTypes.func,
    selectQuickFilterOptions: PropTypes.func
  })
};

export default InsuredsPageHeader;
